import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ChartBarIcon,
  ChevronDownIcon,
  TrophyIcon,
  BanknotesIcon,
  CogIcon,
  NewspaperIcon,
  ArrowRightOnRectangleIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { supabase, getPlayersByTournament, getPartnersByTournament, getTournamentEvents } from '../../lib/supabase';
import { useTournament } from '../TournamentContext';
import { useAppPrefix } from '../AppPrefixContext';
import { toast } from 'react-hot-toast';
import { useCurrentUser } from '../../hooks/useCurrentUser';

interface Tournament {
  id: string;
  name: string;
  status: string;
}

interface DashboardStats {
  players: { total: number; registered: number; pending: number; international: number };
  partners: { total: number; value: number; active: number; pending: number };
  events: { total: number; upcoming: number; completed: number };
}

const defaultStats: DashboardStats = {
  players: { total: 0, registered: 0, pending: 0, international: 0 },
  partners: { total: 0, value: 0, active: 0, pending: 0 },
  events: { total: 0, upcoming: 0, completed: 0 }
};

function formatCurrency(value: number): string {
  if (value === 0) return '₦0';
  if (value >= 1_000_000) return `₦${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `₦${(value / 1_000).toFixed(0)}K`;
  return `₦${value}`;
}

export default function MobileDashboard() {
  const p = useAppPrefix();
  const { tournaments, selectedTournament, setSelectedTournament, loading: tournamentsLoading } = useTournament();
  const navigate = useNavigate();
  const { user: currentUser } = useCurrentUser();
  const [showTournamentPicker, setShowTournamentPicker] = useState(false);
  const [stats, setStats] = useState<DashboardStats>(defaultStats);
  const [revenue, setRevenue] = useState(0);
  const [confirmedPlayers, setConfirmedPlayers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (selectedTournament?.id) {
      fetchDashboardData(selectedTournament.id);
    }
  }, [selectedTournament]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
      toast.success('Signed out');
      navigate(p);
    } catch {
      toast.error('Failed to sign out');
    } finally {
      setSigningOut(false);
    }
  };

  const fetchDashboardData = async (tournamentId: string) => {
    try {
      setLoading(true);

      const { data: tourney } = await supabase
        .from('tournaments')
        .select('registration_fee')
        .eq('id', tournamentId)
        .maybeSingle();

      const { data: paidPlayers } = await supabase
        .from('players')
        .select('id')
        .eq('tournament_id', tournamentId)
        .contains('registration_status', { payment: true });

      const fee = tourney?.registration_fee || 50000;
      const confirmed = paidPlayers?.length || 0;
      setRevenue(fee * confirmed);
      setConfirmedPlayers(confirmed);

      const { data: playersData } = await getPlayersByTournament(tournamentId);
      const { data: partnersData } = await getPartnersByTournament(tournamentId);
      const { data: eventsData } = await getTournamentEvents(tournamentId);

      const now = new Date();

      setStats({
        players: {
          total: playersData?.length || 0,
          registered: playersData?.filter(p => p.status?.toLowerCase() === 'active').length || 0,
          pending: playersData?.filter(p => p.status?.toLowerCase() === 'pending').length || 0,
          international: playersData?.filter(p => p.country && p.country.toLowerCase() !== 'nigeria').length || 0
        },
        partners: {
          total: partnersData?.length || 0,
          value: partnersData?.reduce((sum, p) => sum + (p.contribution || 0), 0) || 0,
          active: partnersData?.filter(p => p.status === 'active').length || 0,
          pending: partnersData?.filter(p => p.status === 'pending').length || 0
        },
        events: {
          total: eventsData?.length || 0,
          upcoming: eventsData?.filter(e => new Date(e.date) > now).length || 0,
          completed: eventsData?.filter(e => new Date(e.date) <= now).length || 0
        }
      });
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const allAdminSections = [
    { tab: 'players',     label: 'Players',     path: `${p}/admin/players`,     icon: UserGroupIcon,            bg: 'bg-blue-500',    desc: 'Registrations' },
    { tab: 'partners',    label: 'Partners',    path: `${p}/admin/partners`,    icon: BuildingOfficeIcon,       bg: 'bg-teal-500',    desc: 'Sponsors' },
    { tab: 'calendar',    label: 'Events',      path: `${p}/admin/events`,      icon: CalendarIcon,             bg: 'bg-rose-500',    desc: 'Calendar' },
    { tab: 'finance',     label: 'Finance',     path: `${p}/admin/finance`,     icon: BanknotesIcon,            bg: 'bg-emerald-500', desc: 'Revenue' },
    { tab: 'analytics',   label: 'Analytics',   path: `${p}/admin/analytics`,   icon: ChartBarIcon,             bg: 'bg-slate-500',   desc: 'Traffic' },
    { tab: 'tournaments', label: 'Tournaments', path: `${p}/admin/tournaments`, icon: TrophyIcon,               bg: 'bg-orange-500',  desc: 'Manage' },
    { tab: 'press',       label: 'Press',       path: `${p}/admin/press`,       icon: NewspaperIcon,            bg: 'bg-cyan-500',    desc: 'Media' },
    { tab: 'contents',    label: 'Content',     path: `${p}/admin/content`,     icon: DocumentTextIcon,         bg: 'bg-violet-500',  desc: 'Pages' },
    { tab: 'courses',     label: 'Courses',     path: `${p}/admin/courses`,     icon: MapPinIcon,               bg: 'bg-lime-600',    desc: 'Golf Courses' },
    { tab: 'scores',      label: 'Scores',      path: `${p}/scorer/groups`,     icon: ClipboardDocumentListIcon, bg: 'bg-amber-500',  desc: 'Scorecards' },
    { tab: 'messaging',   label: 'Messaging',   path: `${p}/admin/messaging`,   icon: ChatBubbleLeftRightIcon,  bg: 'bg-sky-500',     desc: 'Email & Push' },
    { tab: 'settings',    label: 'Settings',    path: `${p}/admin/settings`,    icon: CogIcon,                  bg: 'bg-gray-500',    desc: 'Config' },
  ];

  const adminSections = currentUser
    ? allAdminSections.filter(s => currentUser.allowedTabs.includes(s.tab as any))
    : [];

  if (tournamentsLoading && !selectedTournament) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="pb-10">
      {/* Header Banner */}
      <div className="bg-golf-gray px-4 pt-4 pb-6">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Admin Dashboard</p>
        <div className="relative">
          <button
            onClick={() => setShowTournamentPicker(v => !v)}
            className="flex items-center gap-2"
          >
            <h1 className="text-xl font-display font-bold text-white leading-tight">
              {selectedTournament?.name || 'Select Tournament'}
            </h1>
            <ChevronDownIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
          </button>

          {showTournamentPicker && tournaments.length > 1 && (
            <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl z-20 w-64 overflow-hidden border border-gray-100">
              {tournaments.map(t => (
                <button
                  key={t.id}
                  onClick={() => { setSelectedTournament(t); setShowTournamentPicker(false); }}
                  className={`w-full text-left px-4 py-3 text-sm border-b border-gray-100 last:border-0 ${selectedTournament?.id === t.id ? 'bg-golf-orange/5 text-golf-orange font-semibold' : 'text-golf-gray hover:bg-gray-50'}`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedTournament && (
          <span className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
            selectedTournament.status.toLowerCase() === 'upcoming'
              ? 'bg-golf-orange/20 text-golf-orange'
              : selectedTournament.status.toLowerCase() === 'active'
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-gray-700 text-gray-400'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${
              selectedTournament.status.toLowerCase() === 'upcoming' ? 'bg-golf-orange' :
              selectedTournament.status.toLowerCase() === 'active' ? 'bg-emerald-400' : 'bg-gray-500'
            }`} />
            {selectedTournament.status}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : (
        <div className="px-4 space-y-5 mt-4">

          {/* Key Metrics — 2x2 grid */}
          {(() => {
            const allowed = currentUser?.allowedTabs ?? [];
            const cards = [
              allowed.includes('players') && (
                <div key="players" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-xl bg-blue-500 flex items-center justify-center">
                      <UserGroupIcon className="w-4 h-4 text-white" />
                    </div>
                    {stats.players.pending > 0 && (
                      <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                        {stats.players.pending} pending
                      </span>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-golf-gray">{stats.players.total}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Players</p>
                  <p className="text-xs text-blue-500 font-medium mt-1">{stats.players.registered} active</p>
                </div>
              ),
              allowed.includes('finance') && (
                <div key="revenue" className="bg-golf-gray rounded-2xl p-4">
                  <div className="w-8 h-8 rounded-xl bg-golf-orange/20 flex items-center justify-center mb-2">
                    <ArrowTrendingUpIcon className="w-4 h-4 text-golf-orange" />
                  </div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(revenue)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Revenue</p>
                  <p className="text-xs text-golf-orange font-medium mt-1">{confirmedPlayers} confirmed</p>
                </div>
              ),
              allowed.includes('partners') && (
                <div key="partners" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-xl bg-teal-500 flex items-center justify-center">
                      <BuildingOfficeIcon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-golf-gray">{stats.partners.total}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Partners</p>
                  <p className="text-xs text-teal-500 font-medium mt-1">{formatCurrency(stats.partners.value)} value</p>
                </div>
              ),
              allowed.includes('calendar') && (
                <div key="events" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-xl bg-rose-500 flex items-center justify-center">
                      <CalendarIcon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-golf-gray">{stats.events.total}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Events</p>
                  <p className="text-xs text-rose-500 font-medium mt-1">{stats.events.upcoming} upcoming</p>
                </div>
              ),
            ].filter(Boolean);
            return cards.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">{cards}</div>
            ) : null;
          })()}

          {/* Quick Actions Grid */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Quick Access</p>
            <div className="grid grid-cols-3 gap-2.5">
              {adminSections.map(section => (
                <Link
                  key={section.path}
                  to={section.path}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex flex-col items-center gap-2 active:scale-95 transition-transform"
                >
                  <div className={`w-10 h-10 rounded-xl ${section.bg} flex items-center justify-center`}>
                    <section.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-semibold text-golf-gray leading-tight">{section.label}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{section.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Activity Summary */}
          {(() => {
            const allowed = currentUser?.allowedTabs ?? [];
            const rows = [
              allowed.includes('players') && (
                <div key="players-summary" className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <ClockIcon className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-golf-gray">
                      {stats.players.pending > 0 ? `${stats.players.pending} pending approvals` : 'No pending approvals'}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {stats.players.total > 0 ? `${stats.players.total} total registrations` : 'No players registered yet'}
                    </p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${stats.players.pending > 0 ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-400'}`}>
                    {stats.players.pending}
                  </span>
                </div>
              ),
              allowed.includes('partners') && (
                <div key="partners-summary" className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <BuildingOfficeIcon className="w-4 h-4 text-teal-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-golf-gray">
                      {stats.partners.active > 0 ? `${stats.partners.active} active partners` : 'No active partners'}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {stats.partners.pending > 0 ? `${stats.partners.pending} awaiting confirmation` : 'All partnerships confirmed'}
                    </p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${stats.partners.active > 0 ? 'bg-teal-50 text-teal-600' : 'bg-gray-50 text-gray-400'}`}>
                    {stats.partners.active}
                  </span>
                </div>
              ),
              allowed.includes('calendar') && (
                <div key="events-summary" className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircleIcon className="w-4 h-4 text-rose-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-golf-gray">
                      {stats.events.upcoming > 0 ? `${stats.events.upcoming} upcoming events` : 'No upcoming events'}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {stats.events.completed > 0 ? `${stats.events.completed} completed` : 'No completed events'}
                    </p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${stats.events.upcoming > 0 ? 'bg-rose-50 text-rose-600' : 'bg-gray-50 text-gray-400'}`}>
                    {stats.events.upcoming}
                  </span>
                </div>
              ),
            ].filter(Boolean);
            return rows.length > 0 ? (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Summary</p>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="divide-y divide-gray-50">{rows}</div>
                </div>
              </div>
            ) : null;
          })()}

          {/* Sign Out */}
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl border border-red-100 bg-red-50 active:bg-red-100 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 text-red-500" />
            <span className="text-sm font-semibold text-red-600">
              {signingOut ? 'Signing out...' : 'Sign Out'}
            </span>
          </button>

        </div>
      )}
    </div>
  );
}
